import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { MobileCard } from "./MobileKit";
import { DBATrackingField } from "./DBATrackingCell";

const FIELDS = [
  ["invoice_no", "Invoice"], ["client_code", "Client Code"], ["product", "2nd Name"], ["qty", "Qty"],
  ["customer_name", "Customer"], ["country", "Country"], ["service", "Service"], ["parcel_date", "Parcel Date"],
  ["weight", "Weight (gm)"],
];

export const DbaMobileCards = ({ records, onOpen, isSelected }) => (
  <div className="md:hidden p-2 space-y-2" data-testid="dba-mobile-cards">
    {records.map((r, i) => {
      const first = i === 0 || records[i - 1]?.track_no !== r.track_no;
      return (
        <MobileCard key={i} testid={`dba-card-${i}`} onOpen={() => onOpen(r)} selected={isSelected(r)}
          tone={`${r.cancelled ? "bg-pink-50" : ""} ${first ? "" : "border-l-4 border-l-indigo-200"}`}
          title={r.customer_name || "—"} subtitle={r.track_no || "No tracking"}
          chips={<>
            {r.service ? <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 text-[10px] font-semibold">{r.service}</span>
              : <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-600 text-[10px] font-semibold">NO SERVICE</span>}
            {r.cancelled && <span className="px-1.5 py-0.5 rounded bg-pink-200 text-pink-900 text-[10px] font-bold">CANCELLED</span>}
          </>}
          fields={[["Inv", r.invoice_no], ["Code", r.client_code], ["Item", r.product], ["Qty", String(r.qty ?? 0)],
            ["Country", r.country], ["Date", r.parcel_date], first && ["Wt", r.weight ? `${r.weight} gm` : "0"]]} />
      );
    })}
  </div>
);

// Full edit view on phones: same save path as the inline cells.
export const DbaRecordSheet = ({ record, onClose, onSave, onTrackingSaved, onToggleSelect, selected }) => {
  const [v, setV] = useState({});
  useEffect(() => { if (record) setV(Object.fromEntries(FIELDS.map(([k]) => [k, record[k] ?? ""]))); }, [record]);
  if (!record) return null;
  const save = async () => {
    for (const [k] of FIELDS) {
      if (String(v[k] ?? "") !== String(record[k] ?? "")) await onSave(record, k, k === "service" || k === "client_code" ? String(v[k]).toUpperCase() : v[k]);
    }
    onClose();
  };
  return (
    <Sheet open={!!record} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="bottom" className="max-h-[88vh] overflow-y-auto rounded-t-2xl px-4 pb-8" data-testid="dba-record-sheet">
        <SheetHeader><SheetTitle className="text-left">{record.customer_name || "Record"}</SheetTitle></SheetHeader>
        <div className="mt-2 flex items-center gap-2 text-xs">
          <DBATrackingField rec={record} onSaved={onTrackingSaved} autoStart={!!record._autoEdit} />
          <Button size="sm" variant="outline" className={`h-7 text-xs ${selected ? "border-red-400 text-red-700" : ""}`} onClick={() => onToggleSelect(record)} data-testid="dba-sheet-select">{selected ? "Unselect" : "Select"}</Button>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2">
          {FIELDS.map(([k, label]) => (
            <label key={k} className={`text-[11px] text-slate-500 ${k === "product" || k === "customer_name" ? "col-span-2" : ""}`}>{label}
              <input value={v[k] ?? ""} onChange={(e) => setV({ ...v, [k]: e.target.value })} data-testid={`dba-sheet-${k}`}
                inputMode={k === "qty" || k === "weight" ? "numeric" : undefined}
                className="mt-0.5 w-full px-2 py-2 border border-slate-300 rounded-lg text-sm text-slate-900" />
            </label>
          ))}
        </div>
        <div className="mt-4 flex gap-2">
          <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white" onClick={save} data-testid="dba-sheet-save">Save</Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
